import React, { useState, useEffect } from 'react';
import { Gift, Users, Store, Mail, Check, X, Edit2, Save, RotateCcw, ChevronDown, ChevronRight, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import AdminPageHeader from '../../components/AdminPageHeader';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';

const BenefitsManager = () => {
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(['customer']);
  const [editingValues, setEditingValues] = useState({});

  useEffect(() => {
    fetchBenefits();
  }, []);

  const fetchBenefits = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/benefits/admin/all');
      setCategories(response.data.categories || {});
    } catch (error) {
      toast.error('Fehler beim Laden der Vorteile');
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const toggleBenefit = async (categoryId, benefitId, currentEnabled) => {
    try {
      await axiosInstance.put(
        `/benefits/admin/${categoryId}/benefit/${benefitId}?enabled=${!currentEnabled}`
      );
      
      // Update local state
      setCategories(prev => ({
        ...prev,
        [categoryId]: {
          ...prev[categoryId],
          benefits: prev[categoryId].benefits.map(b =>
            b.id === benefitId ? { ...b, enabled: !currentEnabled } : b
          )
        }
      }));
      
      toast.success('Vorteil aktualisiert');
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const startEditingValue = (categoryId, benefitId, currentValue) => {
    setEditingValues(prev => ({
      ...prev,
      [`${categoryId}-${benefitId}`]: currentValue
    }));
  };

  const cancelEditingValue = (categoryId, benefitId) => {
    setEditingValues(prev => {
      const newState = { ...prev };
      delete newState[`${categoryId}-${benefitId}`];
      return newState;
    });
  };

  const saveValue = async (categoryId, benefitId) => {
    const key = `${categoryId}-${benefitId}`;
    const newValue = editingValues[key];
    
    if (!newValue) return;

    setSaving(true);
    try {
      await axiosInstance.put(
        `/benefits/admin/${categoryId}/benefit/${benefitId}?value=${encodeURIComponent(newValue)}`
      );
      
      // Update local state
      setCategories(prev => ({
        ...prev,
        [categoryId]: {
          ...prev[categoryId],
          benefits: prev[categoryId].benefits.map(b =>
            b.id === benefitId ? { ...b, value: newValue } : b
          )
        }
      }));
      
      cancelEditingValue(categoryId, benefitId);
      toast.success('Wert gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    if (!window.confirm('Alle Vorteile auf Standardwerte zurücksetzen?')) return;
    
    try {
      await axiosInstance.post('/benefits/admin/reset');
      toast.success('Vorteile zurückgesetzt');
      fetchBenefits();
    } catch (error) {
      toast.error('Fehler beim Zurücksetzen');
    }
  };

  const getCategoryIcon = (categoryId) => {
    switch (categoryId) {
      case 'customer': return <Users className="h-5 w-5 text-blue-500" />;
      case 'vendor_basis': return <Store className="h-5 w-5 text-gray-500" />;
      case 'vendor_gold': return <Store className="h-5 w-5 text-amber-500" />;
      case 'newsletter_subscriber': return <Mail className="h-5 w-5 text-pink-500" />;
      default: return <Gift className="h-5 w-5 text-purple-500" />;
    }
  };

  const getCategoryColor = (categoryId) => {
    switch (categoryId) {
      case 'customer': return 'border-l-blue-500';
      case 'vendor_basis': return 'border-l-gray-500';
      case 'vendor_gold': return 'border-l-amber-500';
      case 'newsletter_subscriber': return 'border-l-pink-500';
      default: return 'border-l-purple-500';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">Lädt...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-4 px-3 sm:px-4" style={{ backgroundColor: '#F5EDE4' }}>
      <div className="max-w-4xl mx-auto">
        <AdminPageHeader 
          title="Benefits-Manager" 
          subtitle="Verwalten Sie die Vorteile für verschiedene Benutzergruppen"
          icon={Gift}
        />
        
        {/* Hinweis-Box (Aktionen-Bar mit Zurücksetzen entfernt — wir möchten
            nie versehentlich alle Vorteile-Einstellungen zurücksetzen) */}
        <div className="bg-amber-50/70 border border-amber-100 p-3 rounded-none mb-4 text-sm flex items-start justify-between gap-3 flex-wrap">
          <p className="flex-1 min-w-[200px]">
            Hier können Sie die Vorteile für verschiedene Benutzergruppen verwalten. 
            Schalten Sie Vorteile ein/aus und passen Sie die Werte an (z.B. Rabatt-Prozente).
          </p>
          <Button
            onClick={async () => {
              try {
                const { API } = await import('../../api/axios');
                const r = await fetch(`${API}/benefits/overview-pdf`, {
                  headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
                });
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const blob = await r.blob();
                const url = URL.createObjectURL(blob);
                window.open(url, '_blank');
                setTimeout(() => URL.revokeObjectURL(url), 60000);
              } catch (e) {
                toast.error('PDF-Erstellung fehlgeschlagen: ' + e.message);
              }
            }}
            size="sm"
            className="bg-[#5D4E3C] hover:bg-[#4A3E30] text-white rounded-none whitespace-nowrap"
            data-testid="benefits-pdf-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Übersicht als PDF
          </Button>
        </div>

        {/* Section: Benefits - Goldbraune Überschrift */}
        <div className="mb-3 p-2 px-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
          <div className="w-6 h-6 rounded flex items-center justify-center bg-[#F8F0FF]">
            <Gift className="h-4 w-4 text-[#A070D0]" />
          </div>
          <h2 className="text-base font-semibold text-white">Benutzergruppen</h2>
        </div>

        <div className="space-y-3">
          {Object.entries(categories).map(([categoryId, category]) => {
            const isExpanded = expandedCategories.includes(categoryId);
            
            return (
              <Card key={categoryId} className={`rounded-none border-l-4 ${getCategoryColor(categoryId)}`}>
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(categoryId)}
                  className="w-full flex items-center justify-between p-3 sm:p-4 hover:bg-gray-50 active:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-2 sm:gap-3">
                    {getCategoryIcon(categoryId)}
                    <div className="text-left">
                      <h3 className="font-semibold text-gray-900 text-sm sm:text-base">{category.name}</h3>
                      <p className="text-xs text-gray-500 hidden sm:block">{category.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 hidden sm:inline">
                      {category.benefits?.filter(b => b.enabled).length || 0} / {category.benefits?.length || 0} aktiv
                    </span>
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Benefits List */}
                {isExpanded && (
                  <CardContent className="border-t bg-gray-50 p-0">
                    <div className="divide-y">
                      {category.benefits?.map((benefit) => {
                        const editKey = `${categoryId}-${benefit.id}`;
                        const isEditing = editingValues.hasOwnProperty(editKey);
                        
                        return (
                          <div
                            key={benefit.id}
                            className={`p-3 sm:p-4 flex items-center justify-between gap-2 ${
                              !benefit.enabled ? 'opacity-50' : ''
                            }`}
                          >
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{benefit.name}</p>
                              
                              {/* Value Display/Edit */}
                              <div className="flex items-center gap-2 mt-1">
                                {isEditing ? (
                                  <div className="flex items-center gap-1">
                                    <Input
                                      value={editingValues[editKey]}
                                      onChange={(e) => setEditingValues(prev => ({
                                        ...prev,
                                        [editKey]: e.target.value
                                      }))}
                                      className="h-7 w-24 text-xs rounded-none"
                                    />
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 w-7 p-0"
                                      onClick={() => saveValue(categoryId, benefit.id)}
                                      disabled={saving}
                                    >
                                      <Save className="h-3 w-3 text-green-600" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 w-7 p-0"
                                      onClick={() => cancelEditingValue(categoryId, benefit.id)}
                                    >
                                      <X className="h-3 w-3 text-red-600" />
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1">
                                    <span className="text-xs text-gray-600 bg-white px-2 py-0.5 border rounded-none">
                                      {benefit.value}
                                    </span>
                                    {benefit.editable_value && benefit.enabled && (
                                      <button
                                        onClick={() => startEditingValue(categoryId, benefit.id, benefit.value)}
                                        className="text-gray-400 hover:text-gray-600"
                                      >
                                        <Edit2 className="h-3 w-3" />
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Toggle Switch */}
                            <Switch
                              checked={benefit.enabled}
                              onCheckedChange={() => toggleBenefit(categoryId, benefit.id, benefit.enabled)}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>

        {/* Legend */}
        <div className="mt-6 bg-white p-3 rounded-none text-xs text-gray-600">
          <p className="font-medium mb-2">Legende:</p>
          <div className="grid grid-cols-2 gap-2">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 border-l-4 border-l-blue-500"></div>
              <span>Kunden</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 border-l-4 border-l-gray-500"></div>
              <span>Verkäufer (Basis)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 border-l-4 border-l-amber-500"></div>
              <span>Verkäufer (Gold)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 border-l-4 border-l-pink-500"></div>
              <span>Newsletter-Abonnenten</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BenefitsManager;
